import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
// hooks
import { useShowModal } from 'hooks/useShowModal'
import { useDashboardsAPI } from 'hooks/useDashboardsAPI'
import { Image, useImageUpload } from 'hooks/useImageUpload'
// components
import PopoverModal from 'components/UI/PopoverModal'
// icons
import AddCircle from '@mui/icons-material/AddCircle'
import MenuItemOrigin from '@mui/material/MenuItem'
// themes
import colors from 'themes/colors'
// constants
import {
  WIDGETS,
  FN_DEFAULT_SETTINGS,
  SA_DEFAULT_SETTINGS,
  CARD_DEFAULT_SETTINGS,
  CHART_DEFAULT_SETTINGS,
  SECTION_DEFAULT_SETTINGS,
  SET_UP_ON_ADD_WIDGET_TYPES
} from './constants'
import { Modals } from 'constants/modals'
import { ALLOWED_IMAGE_TYPES } from 'utils/validators'
// store
import { addWidget, currentDashboardSelector, Widget } from 'store/reducers/dashboards'
// views
import { AddFieldButtonWrapper, FieldLabel, Wrapper } from './views'
// types
import { AssetType, DashboardItemType } from 'types/graphql'
// utils
import { getDashboardWidgetIcon } from './utils'

const INPUT_STYLES = { display: 'none' }

const MODAL_PROPS = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'right' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  }
}

const getDefaultSettingsByType = (type: DashboardItemType) => {
  switch (type) {
    case DashboardItemType.Card: {
      return CARD_DEFAULT_SETTINGS
    }
    case DashboardItemType.Section: {
      return SECTION_DEFAULT_SETTINGS
    }
    case DashboardItemType.Function: {
      return FN_DEFAULT_SETTINGS
    }
    case DashboardItemType.StrategyActions: {
      return SA_DEFAULT_SETTINGS
    }
    case DashboardItemType.Chart: {
      return CHART_DEFAULT_SETTINGS
    }
  }
}

interface Props {
  parentWidget?: Widget
}

const AddItemButton: React.FC<Props> = ({ parentWidget }) => {
  const dispatch = useDispatch()

  const { createDashboardItem } = useDashboardsAPI()

  const { showModal: showSettingsModal } = useShowModal({ modalType: Modals.DASHBOARD_WIDGET_SETTINGS_MODAL })

  const currentDashboard = useSelector(currentDashboardSelector)

  const inputFileRef = React.useRef<HTMLInputElement>(null)

  const isStrategyActionsAdded = currentDashboard.widgets?.some(
    (widget) => widget.item.itemType === DashboardItemType.StrategyActions
  )

  const onImageUpload = async (image: Image) => {
    const input = {
      settings: {
        imageUrl: image.url,
        imageFilename: image.name
      },
      itemType: DashboardItemType.Image,
      position: (currentDashboard.widgets?.length || 0) + 1,
      dashboardId: currentDashboard.id,
      ...(parentWidget && { parentDashboardItemId: parentWidget.item.id })
    }

    const { id } = await createDashboardItem(input)

    dispatch(addWidget({ data: {}, item: { id, ...input } }))
  }

  const { onFileUpload } = useImageUpload({
    onUpload: onImageUpload,
    assetType: AssetType.ActivityFile,
    entityPrefix: `entity/${currentDashboard.id}/assets`
  })

  const onUploadClick = () => inputFileRef.current.click()

  const addItem = (type: DashboardItemType, closePopup: () => void) => async () => {
    if (type === DashboardItemType.Image) {
      onUploadClick()
      closePopup()
      return
    }

    const input = {
      ...getDefaultSettingsByType(type),
      itemType: type,
      position: (currentDashboard.widgets?.length || 0) + 1,
      dashboardId: currentDashboard.id,
      ...(parentWidget && { parentDashboardItemId: parentWidget.item.id })
    }

    const { id } = await createDashboardItem(input)

    closePopup()

    const widget = { data: {}, item: { id, ...input } }

    dispatch(addWidget(widget))

    if (SET_UP_ON_ADD_WIDGET_TYPES.includes(type)) {
      showSettingsModal({ widget })
    }
  }

  const widgetsList = WIDGETS.map((widget) => {
    if (
      parentWidget &&
      (widget.type === DashboardItemType.Section || widget.type === DashboardItemType.StrategyActions)
    ) {
      return { ...widget, active: false }
    }

    if (isStrategyActionsAdded && widget.type === DashboardItemType.StrategyActions) {
      return { ...widget, active: false }
    }

    return widget
  })

  return (
    <PopoverModal
      {...MODAL_PROPS}
      trigger={
        <AddFieldButtonWrapper>
          <AddCircle htmlColor={colors.blue500} />
          <span>Add item</span>
          <input
            id='file'
            type='file'
            ref={inputFileRef}
            style={INPUT_STYLES}
            onChange={onFileUpload}
            accept={ALLOWED_IMAGE_TYPES.join(',')}
          />
        </AddFieldButtonWrapper>
      }>
      {({ closePopup }) => (
        <>
          {widgetsList.map((item) => (
            <MenuItemOrigin key={item.type} disabled={!item.active} onClick={addItem(item.type as any, closePopup)}>
              <Wrapper>
                {getDashboardWidgetIcon(item.type)}
                <FieldLabel>{item.label}</FieldLabel>
              </Wrapper>
            </MenuItemOrigin>
          ))}
        </>
      )}
    </PopoverModal>
  )
}

export default AddItemButton
